import { createTheme } from '@mui/material/styles';

/**
 * Centralised colour & style tokens for the entire application.
 * Import `appColors` wherever you need a colour value instead of hardcoding hex strings.
 */
export const appColors = {
  // ── Primary / Brand ──
  primary: {
    main: '#2E8B84',
    dark: '#25736D',
    light: '#E3F2FD',
    accent: '#2563EB',
    accentHover: '#1D4ED8',
  },

  // ── Text hierarchy ──
  text: {
    heading: '#003748',
    body: '#546E7A',
    caption: '#78909C',
    placeholder: '#90A4AE',
    disabled: '#B0BEC5',
  },

  // ── Status ──
  status: {
    success: '#2E7D32',
    successLight: '#4CAF50',
    successBg: '#E8F5E9',
    successBorder: '#A5D6A7',
    error: '#D32F2F',
    errorDark: '#C62828',
    warning: '#E65100',
    warningBg: '#FFF3E0',
    accent: '#7B1FA2',
  },

  // ── Surfaces / Backgrounds ──
  surface: {
    page: '#F5F5F5',
    card: '#FFFFFF',
    input: '#FCFCFC',
    inactive: '#ECEFF1',
  },

  // ── Borders ──
  border: {
    default: '#E0E0E0',
    hover: '#BDBDBD',
    subtle: '#CFD8DC',
    light: '#F5F5F5',
  },

  // ── Sidebar ──
  sidebar: {
    bg: '#f7fafd',
    border: '#e0e0e0',
    text: '#003748',
    textMuted: '#546E7A',
    textDim: '#78909C',
    divider: '#e0e0e0',
    itemHover: 'rgba(0, 131, 127, 0.05)',
    selectedBg: 'rgba(0, 131, 127, 0.12)',
    selectedHover: 'rgba(0, 131, 127, 0.18)',
    activeText: '#003748',
    activeBorder: '#2E8B84',
  },

  // ── Dark buttons (New Incident, etc.) ──
  button: {
    dark: '#5e7097',
    darkHover: '#343b47',
  },

  // ── Top NavBar ──
  navbar: {
    activeBorder: 'rgba(255, 255, 255, 0.5)',
    activeBg: 'rgba(255, 255, 255, 0.15)',
    activeHover: 'rgba(255, 255, 255, 0.25)',
    inactiveHover: 'rgba(255, 255, 255, 0.2)',
  },

  // ── Shadows ──
  shadow: {
    cardHover: '0 2px 8px rgba(0,0,0,0.06)',
    rowHover: '0 2px 8px rgba(0,0,0,0.08)',
  },

  // ── Top header ──
  header: {
    bg: '#00897B',
    text: '#FFFFFF',
    textMuted: 'rgba(255,255,255,0.7)',
    border: 'rgba(255,255,255,0.12)',
  },

  // ── Avatar palette for lists ──
  avatarColors: ['#1565C0', '#7B1FA2', '#2E7D32', '#E65100', '#C62828'],
};

export const defaultTheme = createTheme({
    breakpoints: {
        values: {
            xs: 0,
            sm: 600,
            md: 900,
            lg: 1200,
            xl: 1536,
        },
    },
    palette: {
        primary: {
            main: appColors.primary.main,
            dark: appColors.primary.dark,
        },
        text: {
            primary: appColors.text.heading,
        },
    },
    typography: {
        allVariants: {
            color: '#003748',
        },
        fontSize: 14,
        htmlFontSize: 16,
        h4: {
            '@media (max-width:600px)': {
                fontSize: '1.5rem',
            },
        },
        h5: {
            '@media (max-width:600px)': {
                fontSize: '1.15rem',
            },
        },
    },
    components: {
        MuiButton: {
            defaultProps: {
                variant: 'outlined',
                disableElevation: true,
            },
            styleOverrides: {
                root: {
                    fontSize: '0.875rem',
                    fontWeight: 600,
                    letterSpacing: '0.04em',
                    borderRadius: '4px',
                    minHeight: '34px',
                    padding: '6px 16px',
                    textTransform: 'none',
                    '& .MuiButton-startIcon, & .MuiButton-endIcon': {
                        '& > svg': {
                            fontSize: '18px',
                        },
                    },
                },
                sizeSmall: {
                    minHeight: '32px',
                    padding: '4px 12px',
                    fontSize: '0.8125rem',
                },
                outlined: {
                    borderWidth: '1px',
                    borderColor: appColors.primary.main,
                    color: appColors.primary.main,
                    backgroundColor: 'transparent',
                    '&:hover': {
                        borderColor: appColors.primary.dark,
                        backgroundColor: 'rgba(46, 139, 132, 0.04)',
                    },
                    '&.Mui-disabled': {
                        borderColor: 'rgba(0, 0, 0, 0.26)',
                        color: 'rgba(0, 0, 0, 0.26)',
                    },
                },
                contained: {
                    backgroundColor: appColors.primary.main,
                    color: appColors.surface.card,
                    boxShadow: 'none',
                    '&:hover': {
                        backgroundColor: appColors.primary.dark,
                        boxShadow: 'none',
                    },
                    '&:active': {
                        boxShadow: 'none',
                    },
                    '&.Mui-disabled': {
                        backgroundColor: 'rgba(0, 0, 0, 0.12)',
                        color: 'rgba(0, 0, 0, 0.26)',
                    },
                },
                text: {
                    color: appColors.primary.main,
                    '&:hover': {
                        backgroundColor: 'rgba(21, 101, 192, 0.04)',
                    },
                },
            },
        },
        MuiOutlinedInput: {
            styleOverrides: {
                root: {
                    height: 40,
                    minHeight: 40,
                    fontSize: '14px',
                    backgroundColor: appColors.surface.input,
                    '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: appColors.text.heading,
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: appColors.text.heading,
                        boxShadow: `0 0 0 2px ${appColors.border.default}`,
                    },
                    '& .MuiOutlinedInput-input': {
                        height: 40,
                        minHeight: 40,
                        padding: '0 12px',
                        fontSize: '14px',
                        backgroundColor: appColors.surface.input,
                    },
                },
                input: {
                    height: 40,
                    minHeight: 40,
                    padding: '0 12px',
                    fontSize: '14px',
                    backgroundColor: appColors.surface.input,
                },
            },
        },
        MuiInputAdornment: {
            styleOverrides: {
                root: {
                    borderRadius: '4px 0 0 4px',
                    height: '100%',
                    minHeight: 40,
                    display: 'flex',
                    alignItems: 'center',
                    paddingLeft: 2,
                    paddingRight: 2,
                },
            },
        },
        MuiSelect: {
            styleOverrides: {
                select: {
                    height: 28,
                    minHeight: 28,
                    padding: '0 12px',
                    fontSize: '12px',
                    lineHeight: '40px',
                    backgroundColor: appColors.surface.input,
                },
            },
        },
        MuiInputBase: {
            styleOverrides: {
                input: {
                    fontSize: '14px',
                },
            },
        },
        MuiPaper: {
            styleOverrides: {
                root: {
                    padding: 0,
                },
            },
        },
        MuiInputLabel: {
            styleOverrides: {
                root: {
                    fontSize: '14px',
                    paddingLeft: '8px',
                    color: appColors.text.heading,
                    '&.Mui-focused': {
                        color: appColors.text.heading,
                    },
                    '&.MuiInputLabel-shrink': {
                        color: appColors.text.heading,
                    },
                    '&.Mui-disabled': {
                        color: appColors.text.heading,
                        opacity: 0.5,
                    },
                },
                outlined: {
                    top: '50%',
                    transform: 'translateY(-50%)',
                },
            },
        },
        MuiToolbar: {
            styleOverrides: {
                root: {
                    backgroundColor: appColors.primary.main,
                },
            },
        },
        MuiCard: {
            styleOverrides: {
                root: {
                    backgroundColor: appColors.surface.input,
                },
            },
        },
    },
});
